import {
  AssessmentResponses,
  AssessmentResults,
  RecommendationItem,
  SavingsEstimate,
} from "../../types/assessment/state";
import { logger } from "../../utils/logger";
import { telemetry } from "../../utils/monitoring/telemetry";
import { createPerformanceMonitor } from "../../utils/monitoring/performance";
import {
  MANUAL_PROCESS_PROFILES,
  HOURLY_COST_MIDPOINTS,
  DEFAULT_HOURLY_COST,
  ERROR_IMPACT_SCORES,
  AUTOMATION_GAP_SCORES,
  CHANGE_READINESS_SCORES,
  TIMELINE_SCORES,
  WORKING_WEEKS_PER_YEAR,
} from "../../constants/assessment/options";

const performanceMonitor = createPerformanceMonitor("ResultsCalculator");

/**
 * Computes assessment results from the user's own numbers.
 *
 * Two rules keep the output honest:
 * 1. Savings are derived from what the user reported (hours, headcount, cost
 *    range) with every assumption listed in `savings.assumptions` — never from
 *    invented benchmarks.
 * 2. The opportunity score has no floors or inflation multipliers. A lean,
 *    already-automated business scores low, and that is the correct answer.
 */
export class ResultsCalculator {
  public calculateResults(responses: AssessmentResponses): AssessmentResults {
    const mark = performanceMonitor.start("calculate_results");

    try {
      logger.info("Calculating assessment results", {
        component: "ResultsCalculator",
      });

      const savings = this.calculateSavings(responses);
      const opportunityScore = this.calculateOpportunityScore(responses);
      const insights = this.buildInsights(responses, opportunityScore);
      const structuredRecommendations = this.buildRecommendations(
        responses,
        savings,
      );

      telemetry.track("results_calculated", {
        score: opportunityScore,
        annualSavingsLikely: savings?.annualSavings.likely ?? null,
        recommendationCount: structuredRecommendations.length,
      });

      return {
        score: opportunityScore,
        insights,
        recommendations: structuredRecommendations.map(
          (r) => `${r.title}: ${r.description}`,
        ),
        structuredRecommendations,
        savings: savings ?? undefined,
      };
    } catch (error) {
      logger.error("Error calculating results", {
        component: "ResultsCalculator",
        error,
      });
      throw error;
    } finally {
      performanceMonitor.end(mark);
    }
  }

  /**
   * Savings math from the user's reported numbers. Returns null when the
   * process section wasn't answered (no invented numbers in that case).
   */
  private calculateSavings(
    responses: AssessmentResponses,
  ): SavingsEstimate | null {
    const selected = (responses.manualProcesses || []).filter(
      (p) => MANUAL_PROCESS_PROFILES[p],
    );
    const reportedWeeklyHours = Number(responses.weeklyHours) || 0;
    if (selected.length === 0 || reportedWeeklyHours <= 0) {
      return null;
    }

    const peopleInvolved = Math.max(1, Number(responses.peopleInvolved) || 1);
    // Cap reported hours at the involved people's total working time so a
    // typo can't produce absurd projections.
    const weeklyHours = Math.min(reportedWeeklyHours, peopleInvolved * 40);

    const costKnown = !!(
      responses.hourlyCost &&
      HOURLY_COST_MIDPOINTS[responses.hourlyCost] &&
      responses.hourlyCost !== "Not sure"
    );
    const hourlyCostUsed =
      HOURLY_COST_MIDPOINTS[responses.hourlyCost || ""] ?? DEFAULT_HOURLY_COST;

    // We ask for total hours, not hours per process, to keep the form short —
    // split evenly and say so in the assumptions.
    const hoursPerProcess = weeklyHours / selected.length;

    let weeklyConservative = 0;
    let weeklyLikely = 0;
    const perProcess = selected.map((process) => {
      const share = MANUAL_PROCESS_PROFILES[process].automatableShare;
      weeklyConservative += hoursPerProcess * share.conservative;
      weeklyLikely += hoursPerProcess * share.likely;
      return {
        process,
        weeklyHours: round1(hoursPerProcess),
        automatableShare: share,
        annualSavingsLikely: Math.round(
          hoursPerProcess *
            share.likely *
            hourlyCostUsed *
            WORKING_WEEKS_PER_YEAR,
        ),
      };
    });

    const annualHours = {
      conservative: Math.round(weeklyConservative * WORKING_WEEKS_PER_YEAR),
      likely: Math.round(weeklyLikely * WORKING_WEEKS_PER_YEAR),
    };
    const annualSavings = {
      conservative: Math.round(annualHours.conservative * hourlyCostUsed),
      likely: Math.round(annualHours.likely * hourlyCostUsed),
    };

    const assumptions = [
      `Based on the ${reportedWeeklyHours} hours/week of manual work you reported` +
        (weeklyHours < reportedWeeklyHours
          ? ` (capped at ${weeklyHours} — the total working hours of the ${peopleInvolved} people involved)`
          : ""),
      costKnown
        ? `Labor cost of $${hourlyCostUsed}/hour (midpoint of the range you selected)`
        : `Labor cost assumed at $${hourlyCostUsed}/hour since you didn't specify — adjust mentally if your costs differ`,
      `Hours split evenly across the ${selected.length} process${selected.length > 1 ? "es" : ""} you selected`,
      `${WORKING_WEEKS_PER_YEAR} working weeks/year to allow for holidays and ramp-up`,
      "Automatable share per process is our consulting estimate of a typical well-executed implementation — your actual results depend on your specific workflows",
    ];

    return {
      reportedWeeklyHours,
      hourlyCostUsed,
      annualHours,
      annualSavings,
      assumptions,
      perProcess,
    };
  }

  /**
   * Opportunity score 0-100. Components:
   * - up to 35: share of involved people's time consumed by manual work
   * - up to 25: how un-automated the business currently is
   * - up to 15: breadth of manual processes
   * - up to 15: integration pain
   * - up to 10: error/rework frequency
   */
  private calculateOpportunityScore(responses: AssessmentResponses): number {
    const weeklyHours = Number(responses.weeklyHours) || 0;
    const peopleInvolved = Math.max(1, Number(responses.peopleInvolved) || 1);
    const capacityShare = Math.min(weeklyHours / (peopleInvolved * 40), 1);
    const hoursPoints = capacityShare * 35;

    const automationPoints =
      AUTOMATION_GAP_SCORES[responses.automationLevel || ""] ?? 0;

    const processCount = (responses.manualProcesses || []).length;
    const breadthPoints = Math.min(processCount * 3, 15);

    const integrationCount = (responses.integrationNeeds || []).length;
    const techChallengeCount = (responses.techChallenges || []).length;
    const integrationPoints = Math.min(
      integrationCount * 2 + techChallengeCount * 1.5,
      15,
    );

    const errorPoints = ERROR_IMPACT_SCORES[responses.errorImpact || ""] ?? 0;

    return Math.round(
      Math.max(
        0,
        Math.min(
          100,
          hoursPoints +
            automationPoints +
            breadthPoints +
            integrationPoints +
            errorPoints,
        ),
      ),
    );
  }

  private buildInsights(
    responses: AssessmentResponses,
    opportunityScore: number,
  ): AssessmentResults["insights"] {
    const insights: AssessmentResults["insights"] = [];

    // Process: how much of the involved people's week goes to manual work
    const weeklyHours = Number(responses.weeklyHours) || 0;
    const peopleInvolved = Math.max(1, Number(responses.peopleInvolved) || 1);
    const manualShare = Math.min(weeklyHours / (peopleInvolved * 40), 1);
    const processScore = Math.round(manualShare * 100);
    insights.push({
      category: "process",
      score: processScore,
      recommendations: [
        processScore >= 50
          ? `About ${processScore}% of the involved team's time goes to manual work — your biggest single lever.`
          : `Manual work consumes roughly ${processScore}% of the involved team's time.`,
      ],
    });

    // Technology: automation gap + integration pain, scaled to 100
    const automationGap =
      AUTOMATION_GAP_SCORES[responses.automationLevel || ""] ?? 12;
    const integrationPain = Math.min(
      (responses.integrationNeeds || []).length * 2 +
        (responses.techChallenges || []).length * 1.5,
      15,
    );
    const technologyScore = Math.round(
      ((automationGap + integrationPain) / 40) * 100,
    );
    insights.push({
      category: "technology",
      score: technologyScore,
      recommendations: [
        technologyScore >= 60
          ? "Your current tooling leaves significant automation on the table — integrations and workflow automation are the place to start."
          : "Your tooling is reasonably automated; focus on the remaining manual handoffs.",
      ],
    });

    // Team readiness: how likely changes are to stick
    const readiness =
      CHANGE_READINESS_SCORES[responses.team?.changeReadiness || ""] ?? 50;
    insights.push({
      category: "team",
      score: readiness,
      recommendations: [
        readiness >= 70
          ? "Your team is open to better ways of working — adoption risk is low."
          : "Plan for change management: start with one visible win to build buy-in.",
      ],
    });

    // Urgency: timeline from the goals section
    const urgency =
      TIMELINE_SCORES[responses.socialMedia?.timeline || ""] ?? 50;
    insights.push({
      category: "urgency",
      score: urgency,
      recommendations: [
        urgency >= 80
          ? "You want results quickly — prioritize the low-effort, high-savings items below."
          : "With a longer runway, sequence improvements to compound: integrations first, then workflows.",
      ],
    });

    // Keep insights consistent with the headline score for very lean businesses
    if (opportunityScore < 20) {
      insights[0].recommendations = [
        "Your operation is already lean — automation would help at the margins, not transform it.",
      ];
    }

    return insights;
  }

  /**
   * Recommendations come from what the user actually selected, ordered by
   * estimated dollar impact computed from their own numbers.
   */
  private buildRecommendations(
    responses: AssessmentResponses,
    savings: SavingsEstimate | null,
  ): RecommendationItem[] {
    const recommendations: RecommendationItem[] = [];

    // One recommendation per selected manual process, carrying its estimated impact
    for (const entry of savings?.perProcess || []) {
      const profile = MANUAL_PROCESS_PROFILES[entry.process];
      recommendations.push({
        ...profile.recommendation,
        estimatedAnnualSavings: entry.annualSavingsLikely,
      });
    }
    recommendations.sort(
      (a, b) =>
        (b.estimatedAnnualSavings ?? 0) - (a.estimatedAnnualSavings ?? 0),
    );

    // Integration recommendation when the user reported systems not talking to each other
    const integrationNeeds = responses.integrationNeeds || [];
    const techChallenges = responses.techChallenges || [];
    if (
      integrationNeeds.length >= 2 ||
      techChallenges.includes("Getting Systems to Work Together")
    ) {
      recommendations.push({
        title: "Connect your core systems",
        description:
          "You flagged systems that don't talk to each other. Integrating your core tools removes double entry and is usually the prerequisite for the other automations to work well.",
        effort: "Medium",
        timeline: "3-6 weeks",
      });
    }

    if (techChallenges.includes("Data Accuracy & Reliability")) {
      recommendations.push({
        title: "Establish a single source of truth for your data",
        description:
          "Data accuracy problems usually come from the same information living in several places. Pick the system of record for each data type and sync everything else to it.",
        effort: "Medium",
        timeline: "4-8 weeks",
      });
    }

    return recommendations.slice(0, 6);
  }
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}
